import { z } from 'zod';
import {
  AP_METRIC_DRILL_MAX_PAGE,
  ApCommandCenterRangeSchema,
  ApMetricDefinitionSchema,
} from '@erp/rpc';

/**
 * Command Center URL state. The open tile, its drill-down page, and the
 * invoice open in the slide-in all live in the URL, so Back, Close, reload,
 * and a link opened in a new tab land on the same view.
 */
export const commandCenterSearchSchema = z.object({
  range: ApCommandCenterRangeSchema.optional().catch(undefined),
  /** Metric id of the tile whose invoices are drilled into */
  metric: ApMetricDefinitionSchema.shape.id.optional().catch(undefined),
  /** 1-based drill-down page; absent means the first */
  drillPage: z
    .int()
    .min(1)
    .max(AP_METRIC_DRILL_MAX_PAGE)
    .optional()
    .catch(undefined),
  /** Invoice open in the slide-in over the page */
  invoice: z.uuid().optional().catch(undefined),
});

export type CommandCenterSearch = z.infer<typeof commandCenterSearchSchema>;

/** Toggle a tile: selecting opens its first page, re-selecting closes it. */
export function selectMetricSearch(
  current: CommandCenterSearch,
  metricId: string | null,
): CommandCenterSearch {
  return {
    range: current.range,
    metric: metricId ?? undefined,
    drillPage: undefined,
    invoice: undefined,
  };
}

export function drillPageSearch(
  current: CommandCenterSearch,
  page: number,
): CommandCenterSearch {
  return { ...current, drillPage: page > 1 ? page : undefined };
}

/**
 * Open (or close, with null) an invoice in the slide-in. `page` moves the
 * drill-down along when previous/next crosses a page boundary.
 */
export function invoiceSearch(
  current: CommandCenterSearch,
  invoiceId: string | null,
  page?: number,
): CommandCenterSearch {
  const next = { ...current, invoice: invoiceId ?? undefined };
  return page === undefined ? next : drillPageSearch(next, page);
}
